import React from 'react';
import { Loader2, Send, MailCheck, TriangleAlert } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { emailsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

/**
 * « Tester l'email » — le bouton et sa fenêtre, partagés par tous les écrans de réglage d'email.
 *
 * Demande d'Emeni : pouvoir se poser la question « à quoi ça ressemble une fois reçu ? » sans attendre
 * qu'un vrai client déclenche l'envoi. C'est le geste qu'on trouve dans tous les outils où l'on
 * paramètre des emails, et il manquait ici.
 *
 * CE QUI PART EST CE QUI EST À L'ÉCRAN, pas ce qui est enregistré : on peut écrire trois lignes,
 * tester, corriger, retester, et n'enregistrer qu'une fois satisfait. C'est pour cela que le sujet et
 * le corps sont passés en propriétés plutôt que relus en base par le serveur.
 *
 * Le rendu emprunte exactement la chaîne des vrais envois (marqueurs de mise en forme, jeton {{logo}},
 * en-tête et mentions légales de renderEmail). Les jetons {{…}} reçoivent des valeurs d'exemple : un
 * mail de test qui afficherait « Bonjour {{prenom}} » ne montrerait pas ce que le client voit.
 *
 * Le sujet reçu est préfixé « [Test] » (choix d'Emeni) : le corps reste au caractère près celui du
 * client, mais on ne confond jamais un essai avec un mail réellement parti.
 */

export function TesterEmailModal({ open, onClose, sujet, corps, nomModele }: {
    open: boolean;
    onClose: () => void;
    sujet: string;
    corps: string;
    /** Rappelé dans la fenêtre : sur un écran qui porte dix modèles, il faut savoir lequel on teste. */
    nomModele?: string;
}) {
    const { user } = useAuth();
    const [adresse, setAdresse] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);
    const [resultat, setResultat] = React.useState<{ ok: boolean; texte: string } | null>(null);

    // Pré-remplie avec l'adresse du compte : c'est la destination voulue neuf fois sur dix, et elle
    // reste modifiable pour vérifier le rendu sur une boîte Gmail ou Outlook personnelle.
    React.useEffect(() => {
        if (open) {
            setAdresse(user?.email || '');
            setResultat(null);
        }
    }, [open, user]);

    const envoyer = async () => {
        setEnvoi(true);
        setResultat(null);
        try {
            await emailsApi.envoyerTest({ to: adresse.trim(), subject: sujet, body: corps });
            setResultat({ ok: true, texte: `Email de test envoyé à ${adresse.trim()}.` });
        } catch (e: any) {
            setResultat({ ok: false, texte: e?.message || "L'envoi a échoué." });
        } finally {
            setEnvoi(false);
        }
    };

    const adresseValide = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(adresse.trim());

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="sm"
            title="Tester l'email"
            subtitle={nomModele ? `Modèle « ${nomModele} », tel qu'il est écrit à l'écran.` : "Le modèle tel qu'il est écrit à l'écran, même pas encore enregistré."}
            icon={<Send size={16} />}
            footer={
                <>
                    <Button variant="outline" onClick={onClose}>Fermer</Button>
                    <Button onClick={envoyer} disabled={!adresseValide || envoi}>
                        {envoi ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        {envoi ? 'Envoi…' : 'Envoyer le test'}
                    </Button>
                </>
            }
        >
            <div className="space-y-3">
                <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Adresse de réception</span>
                    <input
                        type="email"
                        value={adresse}
                        onChange={(e) => setAdresse(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && adresseValide && !envoi) envoyer(); }}
                        placeholder="vous@exemple.fr"
                        autoFocus
                        className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content"
                    />
                </label>

                <p className="text-[11.5px] leading-relaxed text-content-muted">
                    Les variables sont remplacées par des valeurs d'exemple, et l'objet reçu commence par
                    « [Test] ». Le reste — mise en forme, logo, couleurs, mentions — est identique à ce que
                    recevra votre client.
                </p>

                {resultat && (
                    <div className={`flex items-start gap-2 rounded-[9px] px-3 py-2.5 text-[12px] ${
                        resultat.ok ? 'bg-state-ok-soft text-state-ok' : 'bg-state-danger-soft text-state-danger'
                    }`}>
                        {resultat.ok ? <MailCheck size={14} className="shrink-0 mt-px" /> : <TriangleAlert size={14} className="shrink-0 mt-px" />}
                        <span>{resultat.texte}</span>
                    </div>
                )}
            </div>
        </Modal>
    );
}

/**
 * Le bouton à poser à côté d'un modèle. Il porte sa propre fenêtre : un écran qui affiche dix modèles
 * pose dix boutons sans avoir à tenir dix états d'ouverture.
 */
export function BoutonTesterEmail({ sujet, corps, nomModele, className = '' }: {
    sujet: string;
    corps: string;
    nomModele?: string;
    className?: string;
}) {
    const [ouvert, setOuvert] = React.useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setOuvert(true)}
                title="S'envoyer ce modèle pour voir le rendu"
                className={`h-7 inline-flex items-center gap-1.5 px-2.5 rounded-full border border-border bg-surface text-[11.5px] font-semibold text-content-muted whitespace-nowrap shrink-0 hover:border-content hover:text-content ${className}`}
            >
                <Send size={12} /> Tester l'email
            </button>
            <TesterEmailModal open={ouvert} onClose={() => setOuvert(false)} sujet={sujet} corps={corps} nomModele={nomModele} />
        </>
    );
}
